import { Button, Checkbox, Chips, Css, IconButton, Tooltip, useModal } from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { observer } from "mobx-react";
import { ConfigureBidGroupsStepQuery } from "src/routes/bids/plan-based-bid-package/components/2.ConfigureBidGroupsStep.generated";
import { BasePlanPriceDimensionsModal } from "src/routes/bids/plan-based-bid-package/components/BasePlanPriceDimensionsModal";
import { BidPlanDimensionRows } from "src/routes/bids/plan-based-bid-package/components/BidPlanDimensionRows";
import { PlanBasedBidPackageFormState } from "src/routes/bids/plan-based-bid-package/utils";

type BasePlanSectionProps = {
  formState: ObjectState<PlanBasedBidPackageFormState>;
  dimensionResults: ConfigureBidGroupsStepQuery["potentialBidPlanDimensions"];
  productOfferings: ConfigureBidGroupsStepQuery["bidPackage"]["productOfferings"];
};

export const BasePlanSection = observer(function BasePlanSection(props: BasePlanSectionProps) {
  const { formState, dimensionResults, productOfferings } = props;
  const { openModal } = useModal();
  const basePlan = formState.bidPlanGroups.rows.find((g) => !g.globalOptionId.value);
  const hasBasePlan = !!basePlan;
  const hasDimensions = !!basePlan?.dimensions.rows.nonEmpty;
  const basePlanChipNames = (formState.productOfferings.value ?? [])
    .map((id) => productOfferings.find((po) => po.id === id)?.name)
    .filter((name): name is string => !!name);

  // Checking adds an empty base plan to the form; unchecking removes it, which the save mutation treats as a delete.
  function toggleBasePlan(selected: boolean) {
    const groups = formState.bidPlanGroups.value ?? [];
    if (selected) {
      const maxOrder = groups.reduce((m, g) => Math.max(m, g.order ?? 0), -1);
      formState.bidPlanGroups.set([...groups, { order: maxOrder + 1, dimensions: [] }]);
    } else {
      formState.bidPlanGroups.set(groups.filter((g) => g.globalOptionId));
    }
  }

  function openDimensionsModal() {
    if (!basePlan) return;
    openModal({ content: <BasePlanPriceDimensionsModal group={basePlan} dimensionResults={dimensionResults} /> });
  }

  return (
    <div css={Css.df.fdc.gap2.mb6.$}>
      <div css={Css.df.aic.jcsb.$}>
        <div css={Css.df.aic.gap2.$}>
          <Checkbox label="Base Plan" checkboxOnly selected={hasBasePlan} onChange={toggleBasePlan} />
          <div css={Css.lg.$}>Base Plan</div>
        </div>
        {hasBasePlan &&
          (hasDimensions ? (
            <IconButton icon="pencil" onClick={openDimensionsModal} />
          ) : (
            <Button icon="plus" label="Add Price Dimensions" variant="secondary" onClick={openDimensionsModal} />
          ))}
      </div>
      {hasBasePlan ? (
        <>
          {basePlanChipNames.nonEmpty && (
            <Tooltip title="Adjust which product offerings to include in this bid in step 1.">
              <div css={Css.wfc.$}>
                <Chips values={basePlanChipNames} />
              </div>
            </Tooltip>
          )}
          {hasDimensions ? (
            <BidPlanDimensionRows group={basePlan} dimensionResults={dimensionResults} />
          ) : (
            <div css={Css.bgGray100.br8.py3.tac.gray700.$}>No price dimensions added.</div>
          )}
        </>
      ) : (
        <div css={Css.bgGray100.br8.py3.tac.gray700.$}>Base plans will not be included in this bid.</div>
      )}
    </div>
  );
});
